import { useState, type ReactNode } from "react";

/**
 * DashboardShell — the OLAP-style chrome the curator and (Phase 2)
 * the admin panels use. Left sidebar with grouped items + a stable
 * top bar with breadcrumb + a slot for a daily-driver quick-action
 * pill (e.g. Set Status). Children render the active section's body.
 *
 * Mobile (≤ 900px): sidebar collapses behind a hamburger; tap reveals
 * it as an overlay drawer with a backdrop. Selecting an item closes
 * the drawer so the user lands on the section without an extra tap.
 *
 * State-driven section render: the parent owns `activeId` and feeds
 * the matching content as `children`. No anchor scrolling — only the
 * selected section is in the DOM, matching the OLAP dashboard
 * pattern (lighter render, focused context, clear info architecture).
 */

export type SidebarItem = {
  /** Stable ID the parent uses to dispatch which content to render. */
  id: string;
  label: string;
  /** Optional small inline icon (SVG / character). Renders in a
   *  16x16 slot left of the label. Omit for text-only items. */
  icon?: ReactNode;
  /** Optional right-aligned badge (e.g. unread count). */
  badge?: string | number;
};

export type SidebarGroup = {
  id: string;
  /** Eyebrow text rendered above the group (small caps gray). Use
   *  short verbs / domains: "Curate" / "People" / "Insights". */
  label: string;
  items: SidebarItem[];
};

export function DashboardShell({
  groups,
  activeId,
  onSelect,
  breadcrumb,
  pageTitle,
  pageSubtitle,
  quickAction,
  contextBadge,
  children,
}: {
  groups: SidebarGroup[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Breadcrumb segments, head-to-tail. The last entry renders bold
   *  as the current location; everything before is muted. */
  breadcrumb: string[];
  pageTitle: string;
  pageSubtitle?: string;
  /** Slot for a daily-driver action pill (e.g. Set Status). Lives in
   *  the top-bar right edge. */
  quickAction?: ReactNode;
  /** Optional small pill rendered next to the page title — useful for
   *  signalling the user's current role context (e.g. "Curator") in
   *  the mega-sidebar world where multiple roles share one sidebar. */
  contextBadge?: ReactNode;
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="dashboard-shell">
      {/* Mobile-only top bar with hamburger. Hidden on desktop where
          the sidebar is always visible. */}
      <div className="dashboard-mobile-bar">
        <button
          type="button"
          className="dashboard-hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
        >
          <span /><span /><span />
        </button>
        <div className="dashboard-mobile-title">{pageTitle}</div>
      </div>

      {/* Sidebar — sticky on desktop, drawer overlay on mobile. */}
      <aside
        className={`dashboard-sidebar ${drawerOpen ? "is-open" : ""}`}
        aria-label="Section navigation"
      >
        <div className="dashboard-sidebar-inner">
          {groups.map((group) => (
            <div key={group.id} className="dashboard-sidebar-group">
              <div className="dashboard-sidebar-eyebrow">{group.label}</div>
              <ul className="dashboard-sidebar-items">
                {group.items.map((item) => {
                  const isActive = item.id === activeId;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`dashboard-sidebar-item ${isActive ? "is-active" : ""}`}
                        onClick={() => {
                          onSelect(item.id);
                          setDrawerOpen(false);
                        }}
                        aria-current={isActive ? "page" : undefined}
                      >
                        {item.icon && (
                          <span className="dashboard-sidebar-icon" aria-hidden="true">
                            {item.icon}
                          </span>
                        )}
                        <span className="dashboard-sidebar-label">{item.label}</span>
                        {item.badge !== undefined && item.badge !== "" && (
                          <span className="dashboard-sidebar-badge">{item.badge}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </aside>

      {/* Backdrop closes the mobile drawer when the user taps outside. */}
      {drawerOpen && (
        <div
          className="dashboard-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main column — breadcrumb + page header + quick action + body. */}
      <main className="dashboard-main">
        <div className="dashboard-main-inner">
          <div className="dashboard-topbar">
            <div className="dashboard-breadcrumb">
              {breadcrumb.map((seg, i) => (
                <span key={i} className={i === breadcrumb.length - 1 ? "is-current" : ""}>
                  {seg}
                  {i < breadcrumb.length - 1 && <span className="sep"> / </span>}
                </span>
              ))}
            </div>
            {quickAction && (
              <div className="dashboard-quick-action">{quickAction}</div>
            )}
          </div>

          <header className="dashboard-page-header">
            <h1 className="dashboard-page-title">
              {pageTitle}
              {contextBadge && (
                <span className="dashboard-context-badge">{contextBadge}</span>
              )}
            </h1>
            {pageSubtitle && (
              <p className="dashboard-page-sub">{pageSubtitle}</p>
            )}
          </header>

          <div className="dashboard-section-body">{children}</div>
        </div>
      </main>
    </div>
  );
}
